import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, NavigationCancel, NavigationEnd } from '@angular/router';
import { BaseService } from './components/mesc/mesc.service';
import { FilterableService } from './components/mesc/filterable-service';


class ModuleUser {
    GMIN: any;
    Name: string;
    ModuleClaims: any[];
    RoleClaims: any[];

    constructor(data: any) {
        if (data) {
            this.GMIN = data.GMIN;
            this.Name = data.Name;
            this.ModuleClaims = data.ModuleClaims;
            this.RoleClaims = data.RoleClaims;
        } else {
            this.reset();
        }
    }

    reset() {
        this.GMIN = null;
        this.Name = null;
        this.ModuleClaims = [];
        this.RoleClaims = [];
    }
}

@Injectable()
export class AuthenticationService {
    private currentUser: any;
    private currentRoute: string;
    private readonly currentModule: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    publicModules: any[] = ['CTWR'];

    public isPHFUser = false;
    public isPHFGuest = false;
    public isPHFUserAdmin = false;
    public isPHFSystemAdmin = false;
    public isPHFProgramAdmin = false;
    public isNotPHFUser = false;
    private readonly userProfileService: FilterableService = new FilterableService();
    moduleService: FilterableService = new FilterableService();

    constructor(
        private readonly service: BaseService,
        private readonly router: Router,
    ) {
        this.userProfileService
            .subscribe(x => {
                if (!x['ModuleClaims']) return;
                this.currentUser = new ModuleUser(x);
                this.router.navigate([this.currentRoute ? this.currentRoute : '']);
            });

        this.service.read('phf/CommonHandler/common/GetUserProfile')
            .then(x => {
                this.userProfileService.next(x);
                if (this.currentRoute) {
                    this.router.navigate([this.currentRoute]);
                }
                else {
                    this.router.navigate(['/']);
                }
            })

        this.router.events.subscribe((e) => {
            if (e instanceof NavigationCancel && !this.currentUser) {
                this.currentRoute = e.url;
            }
            if (e instanceof NavigationEnd) {
                this.currentRoute = e.url;
                this.updateCurrentModule();
            }
        });
    }


    /**
     * 
     */
    isClaimValid(url: string): boolean {
        let response: boolean;
        if (!this.currentUser) return false;

        try {
            const path = url.split('/');
            path.shift();
            if (path[0] && path[0].toLowerCase() === 'mesc') path.shift();
            if (path.length === 0) return true;
            const pathModule = path[0].toLowerCase();

            const isAdminPath: boolean = path.some(x => x.toLowerCase().includes('admin'));
            response = this.isRoleAdmin(path) ||
                !isAdminPath && (
                    this.publicModules.some(m => m.toLowerCase() === pathModule) ||
                    this.currentUser.ModuleClaims.indexOf(path[0]) > -1
                );
        } catch (e) { }
        return response;
    }

    getUserProfile = (): BehaviorSubject<any> => this.userProfileService;

    /**
     * Return module for current route
     */
    getActiveModule = (): BehaviorSubject<any> => this.currentModule;


    /**
     * 
     */
    getUserRoles = () => this.currentUser.RoleClaims;


    /**
     * 
     */
    getUserGMIN = () => this.currentUser.GMIN;


    /**
     * 
     */
    private updateCurrentModule() {
        if (this.moduleService.value.length === 0 && !this.service.loading.value) {
            this.service.read('phf/CommonHandler/common/GetAllModules', false)
                .then(x => {
                    this.moduleService.next(x);
                    this.updateModule();
                })
                .catch(e => this.service.handleError);
        } else {
            this.updateModule();
        }
    }


    /**
     * 
     */
    private updateModule() {
        const path = this.currentRoute.split('/');
        path.shift();
        const match = this.moduleService.value.find(x => x.Name === path[0]);
        if (!this.currentModule.value || this.currentModule.value !== match) this.currentModule.next(match);
    }


    /**
     * 
     */
    private updatePhfRoles() {
        this.currentUser.RoleClaims.forEach(role => {
            const claim: string[] = role.split(':');
            if (claim[1] && claim[0].toLowerCase().includes('phf')) {
                this.phfUserRoles[claim[1].toLowerCase()]();
            }
        })
    }


    /**
    /**
     * 
     * @param path 
     * 
     * example: /MESC/CTWR/Administration
     */
    private isRoleAdmin(path: string[]): boolean {
        return this.currentUser.RoleClaims.some(x => {
            const claim: string[] = x.split(':');
            const role: string = claim.pop().toLowerCase();
            const module: string = claim.pop().toLowerCase();
            const isUserAdminRole = role.includes('admin') && module.includes('user')
            const isMescAdminRole = role.includes('admin') && module.includes('mesc');
            const pathModule = path[0].toLowerCase();
            return (this.moduleAdmins[pathModule] || this.moduleAdmins['default'])(path, isMescAdminRole, isUserAdminRole);
        });
    }


    /**
     * 
     * @param path 
     * @param isMescAdmin 
     * @param isUserAdmin 
     */
    private readonly isMescAdmin = (path: string[], isMescAdmin: boolean, isUserAdmin: boolean): boolean => {
        let isValid = false;
        if (path[1]) {
            if (path[1].toLowerCase() === 'security') {
                isValid = isMescAdmin || isUserAdmin;
            } else if (path[1].toLowerCase() === 'maintenance') {
                isValid = isMescAdmin;
            }
        } else {
            isValid = isMescAdmin || isUserAdmin;
        }
        return isValid
    }


    /**
     * 
     * @param path 
     * @param isMescAdmin 
     * @param isUserAdmin 
     */
    private readonly isPhfAdmin = (path: string[], isMescAdmin: boolean, isUserAdmin: boolean): boolean => {
        const isAdminPath: boolean = path.length > 1 && path[1].toLowerCase() === 'administration';
        let isValid = false;
        this.updatePhfRoles();
        if (isAdminPath && path.length > 2) {
            if (path[2].toLowerCase() === 'users') {
                isValid = this.isPHFUserAdmin;
            } else if (path[2].toLowerCase() === 'program') {
                isValid = this.isPHFProgramAdmin || this.isPHFSystemAdmin;
            } else if (path[2].toLowerCase() === 'upload') {
                isValid = this.isPHFProgramAdmin || this.isPHFSystemAdmin;
            } else {
                isValid = this.isPHFSystemAdmin;
            }
        } else if (path.length > 2 && path[2].toLowerCase() === 'parts') {
            isValid = !this.isPHFUserAdmin;
        } else {
            isValid = this.isPHFUserAdmin || this.isPHFSystemAdmin || this.isPHFProgramAdmin;
        }

        return isValid;
    }


    /**
     * 
     */
    private readonly phfUserRoles = {
        'useradministrator': () => this.isPHFUserAdmin = true,
        'systemadministrator': () => this.isPHFSystemAdmin = true,
        'programadministrator': () => this.isPHFProgramAdmin = true,
        'user': () => this.isPHFUser = true,
        'guest': () => this.isPHFGuest = true
    }


    /**
     * 
     */
    private readonly moduleAdmins = {
        'administration': this.isMescAdmin,
        'phf': this.isPhfAdmin,
        'default': (path, isMescAdmin, isUserAdmin) => isMescAdmin
    }

}
